import { FC, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { PublicOrderFormConfig } from "../../../../types/PublicOrderFormConfig";
import { SettingsRowProps } from "../../components/SettingsRow";
import { HiOutlineClipboardCopy, HiOutlineExternalLink, HiOutlineTrash, HiOutlineCheck } from "react-icons/hi";
import PublicFormConfig from "../../../../models/PublicFormConfig";
import { ToggleSwitch, Button } from "@components";
import { Inline } from "@design-system";

interface GeneralTabProps {
  SettingsRow: React.ComponentType<SettingsRowProps>;
  config: PublicOrderFormConfig;
  logoUrl: string;
  locations: any[];
  handleConfigChange: <K extends keyof PublicOrderFormConfig>(key: K, value: PublicOrderFormConfig[K]) => void;
  onSaveConfig: (configOverride?: Partial<PublicOrderFormConfig>) => Promise<void>;
  setClientId: (value: string) => void;
  setClientSecret: (value: string) => void;
  clientId: string;
  clientSecret: string;
  currentConfigId: string | null; // Needed for slug validation
  onValidationChange: (isValid: boolean) => void; // To disable save button
}

const GeneralTab: FC<GeneralTabProps> = ({
  SettingsRow,
  config,
  logoUrl,
  locations,
  handleConfigChange,
  onSaveConfig,
  clientId,
  clientSecret,
  setClientId,
  setClientSecret,
  currentConfigId,
  onValidationChange,
}) => {
  const { t } = useTranslation(["order_form", "common"]);
  const [copied, setCopied] = useState(false);
  const BASE_URL = `${window.location.protocol}//${window.location.host}/order`;

  // The saved URL persists whatever origin the admin was on when it was saved
  // (e.g. http://localhost:3000 during local dev). Always rebuild it from the
  // current origin so copy/open point at the environment we are actually on.
  const linkForCurrentOrigin = (saved?: string): string => {
    if (!saved) return "";
    try {
      const u = new URL(saved);
      return `${window.location.origin}${u.pathname}${u.search}${u.hash}`;
    } catch {
      return `${window.location.origin}/${saved.replace(/^\/+/, "")}`;
    }
  };

  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'valid' | 'invalid'>('idle');
  const [slugMessage, setSlugMessage] = useState<string | null>(null);

  // Debounced check for slug availability
  useEffect(() => {
    const checkSlug = async () => {
      if (!config.urlSlug) {
        setSlugStatus('idle');
        setSlugMessage(null);
        onValidationChange(true); // No slug is a valid state
        return;
      }

      setSlugStatus('checking');
      const isTaken = await PublicFormConfig.isSlugTaken(config.urlSlug, currentConfigId || '');

      if (isTaken) {
        setSlugStatus('invalid');
        setSlugMessage(t("order_form:url_slug.taken"));
        onValidationChange(false);
      } else {
        setSlugStatus('valid');
        setSlugMessage(t("order_form:url_slug.available"));
        onValidationChange(true);
      }
    };

    const handler = setTimeout(() => {
      checkSlug();
    }, 500); // 500ms debounce

    return () => {
      clearTimeout(handler);
    };
  }, [config.urlSlug, currentConfigId, onValidationChange, t]);


  return (
    <div className="space-y-4">
      <div>
        <div className="divide-y divide-border">
          <SettingsRow
            title={t("order_form:form_url.title")}
            description={t("order_form:form_url.description_short")}
          >
                          <Inline gap={2}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(linkForCurrentOrigin(config.savedUrl));
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  disabled={!config.savedUrl}
                  title={t("common:copy_link")}
                >
                  {copied ? <HiOutlineCheck className="w-4 h-4 mr-1" /> : <HiOutlineClipboardCopy className="w-4 h-4 mr-1" />}
                  {t("common:copy_link")}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => window.open(linkForCurrentOrigin(config.savedUrl), '_blank')}
                  disabled={!config.savedUrl}
                  title={t("common:open_in_new_tab")}
                  icon={<HiOutlineExternalLink className="w-4 h-4" />}
                >
                  {t("common:open_in_new_tab")}
                </Button>
                <button
                  onClick={async () => {
                    setClientId("");
                    setClientSecret("");
                    handleConfigChange("savedUrl", "");
                    handleConfigChange("clientId", "");
                    handleConfigChange("clientSecret", "");
                    try {
                      await onSaveConfig({ 
                        savedUrl: "",
                        clientId: "",
                        clientSecret: ""
                      });
                    } catch (error) {
                      console.error("Error clearing URL and credentials:", error);
                    }
                  }}
                  disabled={!config.savedUrl}
                  className="p-1.5 text-red-400 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed dark:text-red-400 dark:hover:text-red-300"
                  title={t("common:delete")}
                >
                  <HiOutlineTrash className="w-4 h-4" />
                </button>
              </Inline>
          </SettingsRow>

          <SettingsRow
            title={t("order_form:url_slug.toggle_title")}
            description={t("order_form:url_slug.toggle_description")}
          >
            <ToggleSwitch
              checked={config.useCustomUrlSlug}
              onChange={() => handleConfigChange("useCustomUrlSlug", !config.useCustomUrlSlug)}
            />
          </SettingsRow>

          {config.useCustomUrlSlug && (
            <SettingsRow
              title={t("order_form:url_slug.title")}
              description={t("order_form:url_slug.description")}
            >
              <div className="w-full">
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center rounded-l-md border border-r-0 border-gray-300 bg-gray-200 px-3 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-600 dark:text-muted">
                    {BASE_URL}/
                  </span>
                  <input
                    type="text"
                    value={config.urlSlug || ""}
                    onChange={(e) => {
                      const slug = e.target.value
                        .toLowerCase()
                        .replace(/\s+/g, "-") // Replace spaces with -
                        .replace(/[^a-z0-9-]/g, ""); // Remove all non-alphanumeric characters except -
                      handleConfigChange("urlSlug", slug);
                    }}
                    placeholder={t("order_form:url_slug.placeholder")}
                    className="block w-full min-w-0 flex-1 rounded-none rounded-r-md border-gray-300 px-3 py-2 focus:border-brand focus:ring-brand sm:text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-brand"
                  />
                </div>
                {slugMessage && (
                  <p className={`mt-2 text-sm ${
                    slugStatus === 'invalid' ? 'text-red-600 dark:text-red-500' :
                    slugStatus === 'valid' ? 'text-green-600 dark:text-green-500' :
                    'text-muted'
                  }`}>
                    {slugMessage}
                  </p>
                )}
              </div>
            </SettingsRow>
          )}

          <SettingsRow
            title={t("order_form:general_settings.show_prices_with_vat.title")}
            description={t("order_form:general_settings.show_prices_with_vat.description")}
          >
            <ToggleSwitch
              checked={config.showPricesWithVat}
              onChange={() => handleConfigChange("showPricesWithVat", !config.showPricesWithVat)}
            />
          </SettingsRow>

          <SettingsRow
            title={t("order_form:general_settings.allow_product_list_flow.title")}
            description={t("order_form:general_settings.allow_product_list_flow.description")}
          >
            <ToggleSwitch
              checked={config.allowProductListOrderFlow}
              onChange={() => handleConfigChange("allowProductListOrderFlow", !config.allowProductListOrderFlow)}
            />
          </SettingsRow>

          <SettingsRow
            title={t("order_form:general_settings.allow_custom_flow.title")}
            description={t("order_form:general_settings.allow_custom_flow.description")}
          >
            <ToggleSwitch
              checked={config.allowCustomOrderFlow}
              onChange={() => handleConfigChange("allowCustomOrderFlow", !config.allowCustomOrderFlow)}
            />
          </SettingsRow>

          <SettingsRow
            title={t("order_form:general_settings.language.title")}
            description={t("order_form:general_settings.language.description")}
          >
            <select
              value={config.language || "no"}
              onChange={(e) => handleConfigChange("language", e.target.value as "en" | "no")}
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500"
            >
              <option value="no">{t("order_form:language.norwegian")}</option>
              <option value="en">{t("order_form:language.english")}</option>
            </select>
          </SettingsRow>

          {locations.length > 0 && (
            <SettingsRow
              title={t("order_form:general_settings.default_pickup_location.title")}
              description={t("order_form:general_settings.default_pickup_location.description")}
              separator={false}
            >
              <select
                value={config.defaultPickupLocation ? (config.defaultPickupLocation as any).id : ''}
                onChange={(e) => {
                  const selectedId = e.target.value;
                  if (!selectedId) {
                    handleConfigChange('defaultPickupLocation', undefined as any);
                  } else {
                    const loc = locations.find((l) => l.id === selectedId);
                    if (loc) handleConfigChange('defaultPickupLocation', (loc.data ? loc.data() : { ...loc }) as any);
                  }
                }}
                className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500 min-w-48"
              >
                <option value="">{t("order_form:general_settings.default_pickup_location.none")}</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {loc.displayName || loc.addressLine || `${loc.postCode} ${loc.city}`}
                  </option>
                ))}
              </select>
            </SettingsRow>
          )}
        </div>
      </div>

      {/* Logo upload minimal block preserved */}
      {!logoUrl && (
        <SettingsRow
          title={t("order_form:general_settings.logo.upload_title")}
          description={t("order_form:general_settings.logo.upload_description")}
          separator={false}
        >
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const files = e.target.files as FileList | null;
                if (files && files.length > 0) {
                  const file = files[0];
                  const reader = new FileReader();
                  reader.onload = (ev) => {
                    if (ev.target?.result) {
                      handleConfigChange("logoUrl", ev.target.result as string);
                    }
                  };
                  reader.readAsDataURL(file);
                }
              }}
              className="hidden"
            />
            <div className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800">
              {t("order_form:general_settings.logo.upload_button")}
            </div>
          </label>
        </SettingsRow>
      )}

      {logoUrl && (
        <SettingsRow
          title={t("order_form:general_settings.logo.title")}
          description={t("order_form:general_settings.logo.description")}
          separator={false}
        >
          <Inline gap={2}>
            <img 
              src={logoUrl} 
              alt="Logo" 
              className="h-8 w-auto object-contain border border-gray-200 rounded px-2 py-1"
            />
            <button
              onClick={() => handleConfigChange("logoUrl", "")}
              className="p-1 text-red-400 hover:text-red-600"
              title={t("common:remove", "Remove")}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </Inline>
        </SettingsRow>
      )}

      {/* Logo redirect URL */}
      <SettingsRow
        title={t("order_form:general_settings.logo_redirect_url.title")}
        description={t("order_form:general_settings.logo_redirect_url.description")}
        separator={false}
      >
        <input
          type="text"
          value={config.logoRedirectUrl || ""}
          onChange={(e) => handleConfigChange("logoRedirectUrl", e.target.value)}
          placeholder={t("order_form:general_settings.logo_redirect_url.placeholder")}
          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:focus:border-blue-500 dark:focus:ring-blue-500"
        />
      </SettingsRow>
    </div>
  );
};

export default GeneralTab;


